export const parseTestSpec = (specText) => {
  if (!specText) return null;

  const lines = specText.split('\n');
  const result = {
    title: '',
    application: '',
    testerRole: '',
    feature: '',
    ticket: '',
    credentials: [],
    objective: '',
    steps: [],
    expectedResults: [],
    status: '',
    priority: '',
    category: ''
  };

  let currentSection = null;
  let sectionLines = [];

  const extractKeyValue = (line, prefix) => {
    const match = line.match(new RegExp(`^${prefix}:\\s*(.+)$`, 'i'));
    return match ? match[1].trim() : null;
  };

  const finishSection = () => {
    if (!currentSection || sectionLines.length === 0) return;

    const content = sectionLines.join('\n').trim();

    switch (currentSection) {
      case 'credentials':
        sectionLines.forEach(line => {
          const emailMatch = line.match(/Email:\s*(.+)/i);
          const passwordMatch = line.match(/Password:\s*(.+)/i);
          const usernameMatch = line.match(/Username:\s*(.+)/i);
          
          if (emailMatch) {
            result.credentials.push({ type: 'email', label: 'Email', value: emailMatch[1].trim() });
          }
          if (passwordMatch) {
            result.credentials.push({ type: 'password', label: 'Password', value: passwordMatch[1].trim() });
          }
          if (usernameMatch) {
            result.credentials.push({ type: 'username', label: 'Username', value: usernameMatch[1].trim() });
          }
        });
        break;

      case 'objective':
        result.objective = content;
        break;

      case 'steps':
        sectionLines.forEach(line => {
          const stepMatch = line.match(/^(\d+)\.\s*(.+)/);
          if (stepMatch) {
            result.steps.push({
              number: parseInt(stepMatch[1]),
              text: stepMatch[2].trim()
            });
          }
        });
        break;

      case 'results':
        sectionLines.forEach(line => {
          const cleanLine = line.replace(/^[-•]\s*/, '').trim();
          if (cleanLine) {
            result.expectedResults.push(cleanLine);
          }
        });
        break;

      default:
        break;
    }

    sectionLines = [];
    currentSection = null;
  };

  lines.forEach((line, index) => {
    const trimmed = line.trim();

    if (index === 0 && trimmed.startsWith('Test Specification:')) {
      result.title = trimmed.replace('Test Specification:', '').trim();
      return;
    }

    const application = extractKeyValue(trimmed, 'Application');
    if (application) {
      result.application = application;
      return;
    }

    const testerRole = extractKeyValue(trimmed, 'Tester Role');
    if (testerRole) {
      result.testerRole = testerRole;
      return;
    }

    const feature = extractKeyValue(trimmed, 'Feature');
    if (feature) {
      result.feature = feature;
      return;
    }

    const ticket = extractKeyValue(trimmed, 'Ticket');
    if (ticket) {
      result.ticket = ticket;
      return;
    }

    const status = extractKeyValue(trimmed, 'Status');
    if (status) {
      result.status = status;
      return;
    }

    const priority = extractKeyValue(trimmed, 'Priority');
    if (priority) {
      result.priority = priority;
      return;
    }

    const category = extractKeyValue(trimmed, 'Category');
    if (category) {
      result.category = category;
      return;
    }

    if (trimmed.match(/^Test Credentials:/i)) {
      finishSection();
      currentSection = 'credentials';
      return;
    }

    if (trimmed.match(/^Test Objective:/i)) {
      finishSection();
      currentSection = 'objective';
      return;
    }

    if (trimmed.match(/^Test Steps:/i)) {
      finishSection();
      currentSection = 'steps';
      return;
    }

    if (trimmed.match(/^Expected Results:/i)) {
      finishSection();
      currentSection = 'results';
      return;
    }

    if (trimmed.match(/^-+$/)) {
      return;
    }

    if (trimmed.match(/^=+$/)) {
      return;
    }

    if (currentSection && trimmed) {
      sectionLines.push(trimmed);
    }
  });

  finishSection();

  return result;
};

export const formatTimestamp = (ms) => {
  if (ms === undefined || ms === null) return '--:--';
  
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

export const matchEventToStep = (event, steps) => {
  if (!event || !steps || steps.length === 0) return null;

  const eventText = (event.description || event.reasoning || event.step || '').toLowerCase();
  
  let bestMatch = null;
  let bestScore = 0;

  steps.forEach((step, index) => {
    const stepText = step.text.toLowerCase();
    
    const keywords = stepText.split(/\s+/).filter(w => w.length > 3);
    let score = 0;
    
    keywords.forEach(keyword => {
      if (eventText.includes(keyword)) {
        score += 1;
      }
    });

    if (score > bestScore) {
      bestScore = score;
      bestMatch = { ...step, index };
    }
  });

  return bestScore > 0 ? bestMatch : null;
};

export const analyzeExecution = (execution, events, testSpec, realAssertions = []) => {
  const totalSteps = events.filter(e => 
    e.type !== 'snapshot' && e.type !== 'close'
  ).length;
  
  const failedEvents = events.filter(e => e.error || e.status === 'failed');
  const successfulEvents = events.filter(e => 
    !e.error && e.status !== 'failed' && e.type !== 'snapshot' && e.type !== 'close'
  );

  const hasPlaywrightErrors = execution.metadata?.playwrightError || execution.error;
  
  const overallStatus = execution.status === 'failed' || failedEvents.length > 0 || hasPlaywrightErrors
    ? 'FAILED'
    : execution.status === 'completed'
    ? 'PASSED'
    : 'RUNNING';

  const duration = execution.metadata?.duration || 
    (events.length > 0 && events[events.length - 1].videoOffsetMs) || 
    0;

  let expectedCount, passedCount, failedCount;
  
  if (realAssertions.length > 0) {
    expectedCount = realAssertions.length;
    passedCount = realAssertions.filter(a => a.passed).length;
    failedCount = realAssertions.filter(a => !a.passed).length;
  } else {
    expectedCount = testSpec?.expectedResults?.length || 0;
    passedCount = overallStatus === 'PASSED' ? expectedCount : 0;
    failedCount = overallStatus === 'FAILED' ? expectedCount : 0;
  }

  return {
    overallStatus,
    totalSteps,
    successfulSteps: successfulEvents.length,
    failedSteps: failedEvents.length,
    expectedAssertions: expectedCount,
    passedAssertions: passedCount,
    failedAssertions: failedCount,
    duration,
    errors: failedEvents.length + (hasPlaywrightErrors ? 1 : 0)
  };
};
